/* ============================================================
   The command palette — ⌘K. One box that goes anywhere and finds anything:
   pages, projects and tags, a day ("tomorrow", "next friday", via the same
   date parser capture uses), the habits and tasks the mobile Search screen
   finds (the same ranking, from client-core), and the two "new" actions. The
   Things / Linear idiom that makes a desktop app feel fast.
   ============================================================ */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { parseCaptureDate } from '@habishine/shared';
import { dayLabel, type HueName, MIN_QUERY, searchAll, todayStr, useStore, useWeekStart } from '@habishine/client-core';

import { Icon, type IconName } from '../components/Icon';
import { HabitRing, Ring } from '../components/Ring';
import { hueVar } from '../theme/hues';
import { useEditors } from './Editors';

interface Item {
  id: string;
  group: string;
  icon?: IconName;
  iconHue?: HueName;
  leading?: React.ReactNode;
  title: string;
  sub?: string;
  run: () => void;
}

const PAGES: [string, string, IconName, HueName][] = [['Today', '/today', 'sun', 'yellow'], ['Plan', '/plan', 'calendar', 'blue'], ['Review', '/review', 'bar-chart', 'green'], ['Habits', '/habits', 'sparkle', 'teal'], ['Settings', '/settings', 'settings', 'purple']];

export function Palette({ onClose }: { onClose: () => void }) {
  const store = useStore();
  const { editHabit, editProject, openTask, openHabit } = useEditors();
  const { weekStartsOn } = useWeekStart();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [cursor, setCursor] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const today = todayStr();

  const items = useMemo<Item[]>(() => {
    const go = (to: string) => () => navigate(to);
    const query = q.trim();
    const fold = (s: string) => s.toLowerCase();
    const matches = (s: string) => !query || fold(s).includes(fold(query));
    const out: Item[] = [];

    // a day, from the words capture understands
    const date = query.length >= 3 ? parseCaptureDate(query, today, weekStartsOn) : null;
    if (date?.day) {
      out.push({ id: `day-${date.day}`, group: 'Go to', icon: 'calendar', iconHue: 'orange', title: dayLabel(date.day), sub: 'Open this day', run: go(date.day === today ? '/today' : `/today?d=${date.day}`) });
    }

    for (const [title, to, icon, hue] of PAGES) if (matches(title)) out.push({ id: to, group: 'Go to', icon, iconHue: hue, title, run: go(to) });
    for (const p of store.activeProjects()) if (matches(p.name)) out.push({ id: `p-${p.id}`, group: 'Projects', leading: <span className="block h-3 w-3 rounded-full" style={{ background: hueVar(p.hue, 'c') }} />, title: p.name, sub: `${store.projectOpenCount(p.id)} open`, run: go(`/project/${p.id}`) });
    for (const t of store.activeTags()) if (matches(`@${t.name}`) || matches(t.name)) out.push({ id: `t-${t.id}`, group: 'Tags', leading: <span style={{ color: hueVar(t.hue, 'c') }}><Icon name="tag" size={16} /></span>, title: `@${t.name}`, run: go(`/tag/${t.id}`) });

    if (query.length >= MIN_QUERY) {
      const r = searchAll(query, store.state.tasks, store.habitsForDay(today), store.state.tags);
      for (const h of r.habits.slice(0, 6)) out.push({ id: `h-${h.id}`, group: 'Habits', leading: <HabitRing hue={h.hue} pct={h.target > 1 ? h.progress / h.target : h.done ? 1 : 0} emoji={h.emoji} done={h.done} size={28} />, title: h.name, sub: h.done ? 'Done today' : 'Not yet today', run: () => openHabit(h.id, today) });
      for (const hit of r.open.slice(0, 8)) {
        const t = hit.task;
        out.push({ id: `task-${t.id}`, group: 'Tasks', leading: <Ring size={22} stroke={3} hue={store.hueForTask(t)} pct={0} />, title: t.name, sub: [t.scheduledFor ? dayLabel(t.scheduledFor) : 'Backlog', store.projectForTask(t)?.name, hit.step].filter(Boolean).join(' · '), run: () => openTask(t.id, t.scheduledFor ?? today) });
      }
      for (const hit of r.done.slice(0, 3)) out.push({ id: `done-${hit.task.id}`, group: 'Done', leading: <Ring size={22} stroke={3} hue={store.hueForTask(hit.task)} pct={1} check />, title: hit.task.name, sub: hit.task.doneAt ?? undefined, run: () => openTask(hit.task.id, hit.task.doneAt ?? today) });
    }

    if (matches('new habit')) out.push({ id: 'new-habit', group: 'Create', icon: 'plus', iconHue: 'green', title: 'New habit', run: () => editHabit(null) });
    if (matches('new project')) out.push({ id: 'new-project', group: 'Create', icon: 'plus', iconHue: 'pink', title: 'New project', run: () => editProject(null) });
    return out;
  }, [q, store, today, weekStartsOn, navigate, editHabit, editProject, openTask, openHabit]);

  useEffect(() => setCursor(0), [q]);
  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>(`[data-i="${cursor}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [cursor]);

  const pick = (it: Item) => {
    onClose();
    it.run();
  };
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setCursor((c) => Math.min(items.length - 1, c + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setCursor((c) => Math.max(0, c - 1)); }
    else if (e.key === 'Enter') { e.preventDefault(); const it = items[cursor]; if (it) pick(it); }
    else if (e.key === 'Escape') onClose();
  };

  let lastGroup = '';
  return (
    <div className="fade-in fixed inset-0 z-30 flex items-start justify-center bg-[rgba(27,27,31,0.32)] p-3 pt-[max(12px,env(safe-area-inset-top))] backdrop-blur-[6px] md:p-6 md:pt-[14vh]" onMouseDown={onClose}>
      <div role="dialog" aria-label="Command palette" className="surface-pop pop-in w-full max-w-[600px] overflow-hidden rounded-card" onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 border-b border-hairline px-5 py-4">
          <span className="frog-gradient flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-white"><Icon name="search" size={15} strokeWidth={2.6} /></span>
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKey}
            placeholder="Jump to a page, a project, a day… or find a task"
            className="w-full bg-transparent text-[17px] text-ink outline-none placeholder:text-placeholder"
          />
          <kbd className="kbd hidden md:inline">esc</kbd>
          <button type="button" aria-label="Close" onClick={onClose} className="rounded-full p-1.5 text-tertiary md:hidden"><Icon name="x" size={16} /></button>
        </div>
        <div ref={listRef} className="max-h-[60vh] overflow-y-auto p-2 md:max-h-[52vh]">
          {items.length === 0 ? <p className="px-3 py-8 text-center text-sm text-meta">Nothing matches.</p> : null}
          {items.map((it, i) => {
            const head = it.group !== lastGroup ? it.group : null;
            lastGroup = it.group;
            return (
              <React.Fragment key={it.id}>
                {head ? <div className="px-3 pb-1 pt-3 text-[10.5px] font-bold uppercase tracking-[1px] text-tertiary">{head}</div> : null}
                <button
                  type="button"
                  data-i={i}
                  onMouseEnter={() => setCursor(i)}
                  onClick={() => pick(it)}
                  className={`flex w-full items-center gap-3 rounded-[14px] px-3 py-2 text-left transition-colors duration-100 ${i === cursor ? 'bg-frog-soft' : ''}`}
                >
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px]" style={it.iconHue ? { background: hueVar(it.iconHue, 't'), color: hueVar(it.iconHue, 'k') } : undefined}>
                    {it.leading ?? (it.icon ? <Icon name={it.icon} size={16} strokeWidth={2.3} /> : null)}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[14.5px] font-semibold text-ink">{it.title}</span>
                    {it.sub ? <span className="block truncate text-[12px] text-meta">{it.sub}</span> : null}
                  </span>
                  {i === cursor ? <kbd className="kbd">↵</kbd> : null}
                </button>
              </React.Fragment>
            );
          })}
        </div>
        <div className="hidden items-center gap-4 border-t border-hairline px-5 py-2.5 text-[11px] text-tertiary md:flex">
          <span><kbd className="kbd">↑↓</kbd> move</span>
          <span><kbd className="kbd">↵</kbd> open</span>
          <span className="ml-auto">Try “tomorrow”, “next friday”, a task name, @tag</span>
        </div>
      </div>
    </div>
  );
}
